import { useState, useEffect, useCallback, useRef } from 'react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { AlertTriangle, ChevronDown, ChevronUp, CalendarClock, ArrowRight, Truck } from 'lucide-react';
import { Link } from 'react-router-dom';
import { fmtDate } from '@/lib/utils';
import api from '@/lib/api';
import Navbar from '@/components/Navbar';
import UploadZone from '@/components/UploadZone';
import FiltersBar from '@/components/FiltersBar';
import ResultsTable from '@/components/ResultsTable';
import SupplierVisits from '@/components/SupplierVisits';
import { EditProductDialog, DeactivateDialog, ActivateDialog, DeleteDialog } from '@/components/ProductDialogs';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import UploadHistory from '@/components/UploadHistory';

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [dashboardData, setDashboardData] = useState({ data: [], total: 0, page: 1, total_pages: 0, buffer: 10 });
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ supplier: 'all', dateFrom: null, dateTo: null, search: '' });
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [buffer, setBuffer] = useState(10);
  const [slowDays, setSlowDays] = useState(30);
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState('total_sold');
  const [sortOrder, setSortOrder] = useState('desc');
  const [showMode, setShowMode] = useState('all');
  const [duplicates, setDuplicates] = useState([]);
  const [merging, setMerging] = useState(false);
  const [dueData, setDueData] = useState(null);
  const [showAllDue, setShowAllDue] = useState(false);
  const [todayData, setTodayData] = useState(null);
  const [uploadOpen, setUploadOpen] = useState(true);
  const [historyKey, setHistoryKey] = useState(0);
  const searchTimerRef = useRef(null);

  // Debounce search input - only fire API after 600ms of no typing
  useEffect(() => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      setDebouncedSearch(filters.search);
    }, 600);
    return () => { if (searchTimerRef.current) clearTimeout(searchTimerRef.current); };
  }, [filters.search]);

  // Product management dialogs
  const [editProduct, setEditProduct] = useState(null);
  const [deactivateProduct, setDeactivateProduct] = useState(null);
  const [activateProduct, setActivateProduct] = useState(null);
  const [deleteProduct, setDeleteProduct] = useState(null);

  const fetchStats = useCallback(async () => {
    try {
      const { data } = await api.get('/stats');
      setStats(data);
    } catch (e) { console.error('Dashboard request failed:', e); }
  }, []);

  const fetchSuppliers = useCallback(async () => {
    try {
      const { data } = await api.get('/suppliers');
      setSuppliers(data);
    } catch (e) { console.error('Dashboard request failed:', e); }
  }, []);

  const fetchSettings = useCallback(async () => {
    try {
      const { data } = await api.get('/settings');
      setBuffer(data.buffer_percentage);
      if (data.slow_product_days) setSlowDays(data.slow_product_days);
    } catch (e) { console.error('Dashboard request failed:', e); }
  }, []);

  const fetchDuplicates = useCallback(async () => {
    try {
      const { data } = await api.get('/duplicates');
      setDuplicates(data);
    } catch (e) { console.error('Dashboard request failed:', e); }
  }, []);

  const fetchDue = useCallback(async () => {
    try {
      const { data } = await api.get('/suppliers/due');
      setDueData(data);
    } catch (e) { console.error('Dashboard request failed:', e); }
  }, []);

  const fetchToday = useCallback(async () => {
    try {
      const { data } = await api.get('/suppliers/today');
      setTodayData(data);
    } catch (e) { console.error('Dashboard request failed:', e); }
  }, []);

  const handleSkipDue = async (supplier) => {
    try {
      await api.post('/suppliers/due/snooze', { supplier });
      toast.success(`${supplier} skipped for this week`);
      fetchDue();
    } catch {
      toast.error('Could not skip — try again');
    }
  };

  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page,
        limit: 50,
        buffer,
        sort_by: sortBy,
        sort_order: sortOrder,
        show_mode: showMode
      };
      if (filters.supplier !== 'all') params.supplier = filters.supplier;
      if (filters.dateFrom) params.date_from = format(filters.dateFrom, 'yyyy-MM-dd');
      if (filters.dateTo) params.date_to = format(filters.dateTo, 'yyyy-MM-dd');
      if (debouncedSearch) params.search = debouncedSearch;

      const { data } = await api.get('/dashboard', { params });
      setDashboardData(data);
    } catch {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, [filters.supplier, filters.dateFrom, filters.dateTo, debouncedSearch, buffer, page, sortBy, sortOrder, showMode, slowDays]);

  useEffect(() => {
    fetchStats();
    fetchSuppliers();
    fetchSettings();
    fetchDuplicates();
    fetchDue();
    fetchToday();
  }, [fetchStats, fetchSuppliers, fetchSettings, fetchDuplicates, fetchDue, fetchToday]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  useEffect(() => {
    setPage(1);
  }, [filters.supplier, filters.dateFrom, filters.dateTo, debouncedSearch, buffer, showMode]);

  const handleUploadSuccess = () => {
    fetchStats();
    fetchSuppliers();
    fetchDashboard();
    fetchDuplicates();
    setHistoryKey((k) => k + 1);
  };

  const handleBufferChange = async (val) => {
    const clamped = Math.min(100, Math.max(0, val || 0));
    setBuffer(clamped);
    try {
      await api.put('/settings/buffer', { value: clamped });
    } catch (e) { console.error('Dashboard request failed:', e); }
  };

  const handleMergeDuplicates = async () => {
    setMerging(true);
    try {
      const { data } = await api.post('/duplicates/merge-all');
      toast.success(`Merged ${data.merged} duplicate products (${data.rows_updated.toLocaleString()} records normalized)`);
      fetchDuplicates();
      fetchDashboard();
      fetchStats();
    } catch {
      toast.error('Merge failed — please try again');
    } finally {
      setMerging(false);
    }
  };

  const handleSlowDaysChange = async (val) => {
    setSlowDays(val);
    try {
      await api.put('/settings/slow-days', { value: val });
      toast.success(`Slow/Dead badge now applies after ${val} days without sales`);
    } catch (e) { console.error('Dashboard request failed:', e); }
  };

  const handleSort = (field, order) => {
    setSortBy(field);
    setSortOrder(order);
  };

  const handleExport = async (type) => {
    try {
      const response = await api.get(`/export/${type}`, {
        params: {
          buffer,
          supplier: filters.supplier !== 'all' ? filters.supplier : undefined,
          date_from: filters.dateFrom ? format(filters.dateFrom, 'yyyy-MM-dd') : undefined,
          date_to: filters.dateTo ? format(filters.dateTo, 'yyyy-MM-dd') : undefined,
          search: filters.search || undefined
        },
        responseType: 'blob'
      });
      const contentDisposition = response.headers['content-disposition'];
      let filename = `purchase_order.${type === 'csv' ? 'csv' : 'xlsx'}`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename=(.+)/);
        if (match) filename = match[1];
      }
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${type.toUpperCase()} file downloaded`);
    } catch {
      toast.error(`Failed to export ${type.toUpperCase()}`);
    }
  };

  const refreshAfterProductChange = () => {
    fetchDashboard();
    fetchStats();
    fetchSuppliers();
  };

  return (
    <div className="min-h-screen bg-white page-enter">
      <Navbar stats={stats} />

      <main className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        {/* Upload Section */}
        <div>
          <button
            data-testid="upload-toggle"
            onClick={() => setUploadOpen(!uploadOpen)}
            className="flex items-center gap-2 mb-3 text-sm font-bold text-zinc-900 hover:text-emerald-700 transition-colors"
            style={{ fontFamily: "'Chivo', sans-serif" }}
          >
            Upload Data
            {uploadOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
          {uploadOpen && (
            <Tabs defaultValue="sales">
              <TabsList className="rounded-none h-9 mb-2">
                <TabsTrigger data-testid="sales-upload-tab" value="sales" className="rounded-none text-xs px-4">
                  Sales Data
                </TabsTrigger>
                <TabsTrigger data-testid="purchase-upload-tab" value="purchase" className="rounded-none text-xs px-4">
                  Purchase Report
                </TabsTrigger>
              </TabsList>
              <TabsContent value="sales">
                <UploadZone mode="sales" onUploadSuccess={handleUploadSuccess} />
              </TabsContent>
              <TabsContent value="purchase">
                <UploadZone mode="purchase" onUploadSuccess={handleUploadSuccess} />
              </TabsContent>
            </Tabs>
          )}
          {uploadOpen && <div className="mt-3"><UploadHistory refreshKey={historyKey} /></div>}
        </div>

        {/* Expected today */}
        {todayData?.visitors?.length > 0 && (
          <div data-testid="today-visitors-panel" className="border border-emerald-200 bg-emerald-50/40 p-4">
            <div className="flex items-center gap-2 mb-2">
              <Truck className="h-4 w-4 text-emerald-600" />
              <span className="text-xs font-bold tracking-[0.08em] uppercase text-emerald-700">
                Expected Today · <span className="capitalize">{todayData.weekday}</span> ({todayData.visitors.length})
              </span>
            </div>
            <div className="space-y-1">
              {todayData.visitors.map((v) => (
                <div key={v.supplier} className="flex items-center justify-between text-xs text-emerald-900 py-0.5">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="font-semibold truncate">{v.supplier}</span>
                    <span className="text-emerald-600 capitalize shrink-0">{v.frequency}</span>
                    {v.needs_order === true && (
                      <span className="text-[10px] font-semibold text-amber-700 bg-amber-100 border border-amber-200 px-1.5 py-0 shrink-0">probably needs an order</span>
                    )}
                    {v.needs_order === false && (
                      <span className="text-[10px] text-zinc-500 bg-zinc-100 border border-zinc-200 px-1.5 py-0 shrink-0">recently ordered</span>
                    )}
                  </div>
                  <Link
                    data-testid={`today-open-${v.supplier}`}
                    to={`/supplier-view?supplier=${encodeURIComponent(v.supplier)}`}
                    className="flex items-center gap-1 text-[11px] font-semibold text-emerald-700 hover:text-emerald-900 shrink-0 ml-3"
                  >
                    Prepare <ArrowRight className="h-3 w-3" />
                  </Link>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Probably due for ordering */}
        {dueData?.due?.length > 0 && (
          <div data-testid="due-suppliers-panel" className="border border-sky-200 bg-sky-50/40 p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <CalendarClock className="h-4 w-4 text-sky-600" />
                <span className="text-xs font-bold tracking-[0.08em] uppercase text-sky-700">
                  Probably Due For Ordering ({dueData.due.length})
                </span>
              </div>
              <span className="text-[10px] text-sky-500">learned from your purchase history · {dueData.total_tracked} suppliers tracked</span>
            </div>
            <div className="space-y-1">
              {(showAllDue ? dueData.due : dueData.due.slice(0, 8)).map((d) => (
                <div key={d.supplier} className="flex items-center justify-between text-xs text-sky-900 py-0.5">
                  <div className="flex items-baseline gap-2 min-w-0">
                    <span className="font-semibold truncate">{d.supplier}</span>
                    <span className="text-sky-600 shrink-0">
                      last ordered <span className="font-mono font-semibold">{d.days_since}d</span> ago ({fmtDate(d.last_ordered)}) · usually every ~{d.cycle_days}d
                    </span>
                  </div>
                  <div className="flex items-center gap-2 shrink-0 ml-3">
                    <button
                      data-testid={`due-skip-${d.supplier}`}
                      onClick={() => handleSkipDue(d.supplier)}
                      className="text-[11px] font-medium text-zinc-400 hover:text-zinc-700 border border-transparent hover:border-zinc-300 px-1.5 py-0.5 transition-colors"
                      title="Hide from this list for 7 days"
                    >
                      Skip
                    </button>
                    <Link
                      data-testid={`due-open-${d.supplier}`}
                      to={`/supplier-view?supplier=${encodeURIComponent(d.supplier)}`}
                      className="flex items-center gap-1 text-[11px] font-semibold text-sky-700 hover:text-sky-900"
                    >
                      Open <ArrowRight className="h-3 w-3" />
                    </Link>
                  </div>
                </div>
              ))}
              {dueData.due.length > 8 && (
                <button
                  data-testid="due-show-all-btn"
                  onClick={() => setShowAllDue((v) => !v)}
                  className="text-xs text-sky-600 hover:text-sky-800 font-medium mt-1"
                >
                  {showAllDue ? 'Show less' : `Show all ${dueData.due.length}`}
                </button>
              )}
            </div>
          </div>
        )}

        {/* Duplicate Warnings */}
        {duplicates.length > 0 && (
          <div data-testid="duplicate-warning" className="border border-amber-200 bg-amber-50/50 p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-amber-600" />
                <span className="text-xs font-bold tracking-[0.08em] uppercase text-amber-700">
                  Duplicate Products Detected ({duplicates.length})
                </span>
              </div>
              <button
                data-testid="merge-duplicates-btn"
                onClick={handleMergeDuplicates}
                disabled={merging}
                className="text-xs font-semibold px-3 py-1.5 bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50 transition-colors"
              >
                {merging ? 'Merging…' : 'Merge All'}
              </button>
            </div>
            <div className="space-y-1.5">
              {duplicates.slice(0, 5).map((d) => (
                <div key={d.barcode} className="text-xs text-amber-800 flex gap-2">
                  <span className="font-mono text-amber-600">{d.barcode}</span>
                  <span>has {d.names.length} names: {d.names.join(', ')}</span>
                  {d.suppliers.length > 1 && <span className="text-amber-500">({d.suppliers.length} suppliers)</span>}
                </div>
              ))}
              {duplicates.length > 5 && (
                <p className="text-xs text-amber-500 mt-1">...and {duplicates.length - 5} more</p>
              )}
            </div>
          </div>
        )}

        {/* Filters */}
        <FiltersBar
          filters={filters}
          setFilters={setFilters}
          suppliers={suppliers}
          buffer={buffer}
          onBufferChange={handleBufferChange}
          slowDays={slowDays}
          onSlowDaysChange={handleSlowDaysChange}
          showMode={showMode}
          onShowModeChange={setShowMode}
        />

        {/* Results */}
        <ResultsTable
          data={dashboardData.data}
          loading={loading}
          page={page}
          totalPages={dashboardData.total_pages}
          total={dashboardData.total}
          buffer={buffer}
          sortBy={sortBy}
          sortOrder={sortOrder}
          showMode={showMode}
          onSort={handleSort}
          onPageChange={setPage}
          onExportCSV={() => handleExport('csv')}
          onExportExcel={() => handleExport('excel')}
          onEditProduct={setEditProduct}
          onDeactivateProduct={setDeactivateProduct}
          onActivateProduct={setActivateProduct}
          onDeleteProduct={setDeleteProduct}
        />

        {/* Supplier Visit Schedule */}
        <SupplierVisits />
      </main>

      {/* Product Management Dialogs */}
      {editProduct && (
        <EditProductDialog
          product={editProduct}
          open={!!editProduct}
          onOpenChange={(v) => { if (!v) setEditProduct(null); }}
          onSuccess={refreshAfterProductChange}
        />
      )}
      {deactivateProduct && (
        <DeactivateDialog
          product={deactivateProduct}
          open={!!deactivateProduct}
          onOpenChange={(v) => { if (!v) setDeactivateProduct(null); }}
          onSuccess={refreshAfterProductChange}
        />
      )}
      {activateProduct && (
        <ActivateDialog
          product={activateProduct}
          open={!!activateProduct}
          onOpenChange={(v) => { if (!v) setActivateProduct(null); }}
          onSuccess={refreshAfterProductChange}
        />
      )}
      {deleteProduct && (
        <DeleteDialog
          product={deleteProduct}
          open={!!deleteProduct}
          onOpenChange={(v) => { if (!v) setDeleteProduct(null); }}
          onSuccess={refreshAfterProductChange}
        />
      )}
    </div>
  );
}
